import { useMemo } from "react";
import type { ScenarioBridgeStep, ScenarioValueUnit } from "../../../services/api";
import { formatAmount } from "../scenarioFormat";
import { layoutBridge } from "../scenarioLayout";

/** A horizontal waterfall from today to the scenario. */
export function ScenarioBridge({
  steps,
  unit = "currency",
  title = "Bridge",
}: {
  steps: ScenarioBridgeStep[];
  unit?: ScenarioValueUnit;
  title?: string;
}) {
  const layout = useMemo(() => layoutBridge(steps), [steps]);
  if (steps.length === 0) return null;
  const range = layout.high - layout.low;
  const position = (value: number) => ((value - layout.low) / range) * 100;

  return (
    <figure className="scenario-bridge" aria-label={title}>
      <ol>
        {layout.bars.map((bar, index) => {
          // A total stands on the axis: zero, or the zoomed floor.
          const from =
            bar.kind === "total" ? (layout.zoomed ? layout.low : 0) : bar.start;
          const left = position(Math.min(from, bar.end));
          const width = Math.max(
            Math.abs(position(bar.end) - position(from)),
            0.6,
          );
          return (
            <li key={`${bar.label}-${index}`} className={`bridge-row ${bar.kind}`}>
              <span className="bridge-label">{bar.label}</span>
              <span className="bridge-track">
                <span
                  className="bridge-bar"
                  style={{ left: `${left}%`, width: `${width}%` }}
                />
              </span>
              <span className="bridge-value">
                {bar.kind !== "total" && bar.value > 0 ? "+" : ""}
                {formatAmount(bar.value, unit)}
              </span>
            </li>
          );
        })}
      </ol>
      {layout.zoomed && (
        <figcaption>
          Axis starts at {formatAmount(layout.low, unit)} so the changes are
          visible.
        </figcaption>
      )}
    </figure>
  );
}
